import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Search, Truck, LayoutDashboard, ChevronRight } from 'lucide-react';
import { useClerk } from '@clerk/clerk-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/ui/dialog';
import { DataTableShell } from '@/shared/ui/data-table-shell';
import { QueryErrorMessage } from '@/shared/ui/QueryErrorMessage';
import { RoleBadge, UserStatusBadge } from '@/modules/users';
import { useAllUsersForImpersonation, useImpersonateUser } from '@/hooks/useImpersonateUser';
import { type UseQueryResult } from '@/lib/query';
import {
  ImpersonationUserDto,
  ImpersonationError,
  ImpersonationErrorCode,
} from '@cargovibe/shared-types';
import { formatFullName } from '@/shared/services/formatFullName';

function withoutTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

const MOBILE_APP_URL: string = import.meta.env.VITE_MOBILE_APP_URL;

if (!MOBILE_APP_URL) {
  throw new Error('Missing required environment variable: VITE_MOBILE_APP_URL');
}

const DRIVER_APP_URL: string = withoutTrailingSlash(MOBILE_APP_URL);

/**
 * Testing-only destination picker: lets an Admin choose which app redeems
 * the ticket, so both a Driver landing in `apps/mobile` and a CompanyAdmin
 * landing here can be verified manually. Replace with an automatic
 * redirect based on the target user's role once that's settled.
 */
interface Destination {
  name: string;
  url: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  onSelect: () => void;
}

export function ImpersonateUserPanel(): React.JSX.Element {
  const { t } = useTranslation();
  const { signOut } = useClerk();
  const { mutateAsync: impersonate, isPending } = useImpersonateUser();

  const [error, setError] = useState<string | null>(null);
  const [ticket, setTicket] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const {
    data: users,
    isLoading,
    error: usersError,
  }: UseQueryResult<ImpersonationUserDto[]> = useAllUsersForImpersonation();

  type UserWithFormattedName = ImpersonationUserDto & { formattedName: string };

  const filteredUsers: UserWithFormattedName[] = useMemo<UserWithFormattedName[]>(() => {
    const usersWithFormattedName: UserWithFormattedName[] = (users ?? []).map(
      (user: ImpersonationUserDto): UserWithFormattedName => ({
        ...user,
        formattedName: formatFullName(user.firstname, user.lastname),
      })
    );
    const lowerSearchTerm: string = searchTerm.toLowerCase();
    if (!lowerSearchTerm) return usersWithFormattedName;
    return usersWithFormattedName.filter(
      (user: UserWithFormattedName): boolean =>
        user.formattedName.toLowerCase().includes(lowerSearchTerm) ||
        (user.email?.toLowerCase().includes(lowerSearchTerm) ?? false)
    );
  }, [users, searchTerm]);

  const handleSelectDestination = (redeemUrl: string): void => {
    if (!ticket) return;
    void signOut(() => {
      window.location.href = `${redeemUrl}?ticket=${encodeURIComponent(ticket)}`;
    });
  };

  const destinations: Destination[] = [
    {
      name: t('impersonate.destinationDriverApp'),
      url: DRIVER_APP_URL,
      icon: Truck,
      onSelect: (): void => {
        handleSelectDestination(`${DRIVER_APP_URL}/impersonateUser`);
      },
    },
    {
      name: t('impersonate.destinationAdminPortal'),
      url: window.location.origin,
      icon: LayoutDashboard,
      onSelect: (): void => {
        handleSelectDestination(`${window.location.origin}/impersonateUser`);
      },
    },
  ];

  const runImpersonate = async (targetExternalAuthId: string): Promise<void> => {
    setError(null);

    try {
      const newTicket: string = await impersonate(targetExternalAuthId);
      setTicket(newTicket);
    } catch (submitError: unknown) {
      console.error('[ImpersonateUserPanel]', submitError);
      if (submitError instanceof ImpersonationError) {
        switch (submitError.errorCode) {
          case ImpersonationErrorCode.FeatureDisabled:
            setError(t('impersonate.errorFeatureDisabled'));
            break;
          case ImpersonationErrorCode.ClerkUserMissing:
            setError(t('impersonate.errorClerkUserMissing'));
            break;
          case ImpersonationErrorCode.UserNotFound:
            setError(t('impersonate.errorUserNotFound'));
            break;
          case ImpersonationErrorCode.TokenCreationFailed:
            setError(t('impersonate.errorTokenCreationFailed'));
            break;
          case ImpersonationErrorCode.Unknown:
            setError(t('impersonate.errorUnknown'));
            break;
        }
      } else {
        setError(t('impersonate.errorGeneral'));
      }
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
  };

  const handleImpersonateRow = (user: ImpersonationUserDto) => (): void => {
    if (!user.externalAuthId) {
      setError(t('impersonate.noExternalAuthId'));
      return;
    }
    void runImpersonate(user.externalAuthId);
  };

  const handleDialogOpenChange = (open: boolean): void => {
    if (!open) setTicket(null);
  };

  return (
    <>
      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      {usersError ? (
        <QueryErrorMessage error={usersError} />
      ) : (
        <div className="space-y-4">
          <div className="relative max-w-md">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/60"
            />
            <Input
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-9"
            />
          </div>

          <DataTableShell
            icon={Users}
            count={filteredUsers.length}
            countLabel={t('users.userLabel')}
            isLoading={isLoading}
            isEmpty={filteredUsers.length === 0}
          >
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-muted-foreground bg-muted/50 border-b border-border uppercase">
                <tr>
                  <th className="px-6 py-3">{t('users.name')}</th>
                  <th className="px-6 py-3">{t('users.email')}</th>
                  <th className="px-6 py-3">{t('users.role')}</th>
                  <th className="px-6 py-3">{t('users.status')}</th>
                  <th className="px-6 py-3 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 font-medium text-foreground">{user.formattedName}</td>
                    <td className="px-6 py-4 text-muted-foreground">{user.email || '-'}</td>
                    <td className="px-6 py-4">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-6 py-4">
                      <UserStatusBadge status={user.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button size="sm" disabled={isPending} onClick={handleImpersonateRow(user)}>
                        {t('impersonate.button')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableShell>
        </div>
      )}

      <Dialog open={ticket !== null} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('impersonate.redeemDialogTitle')}</DialogTitle>
            <DialogDescription>{t('impersonate.redeemDialogDescription')}</DialogDescription>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-3">
            {destinations.map((destination) => (
              <Button
                key={destination.url}
                className="h-auto w-full min-w-0 justify-between gap-3 rounded-xl px-4 py-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                onClick={destination.onSelect}
              >
                <span className="flex min-w-0 flex-1 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15">
                    <destination.icon size={18} className="text-primary-foreground" />
                  </span>
                  <span className="flex min-w-0 flex-col items-start text-left">
                    <span className="text-sm font-semibold text-primary-foreground">
                      {destination.name}
                    </span>
                    <span className="w-full truncate text-xs text-primary-foreground/70">
                      {destination.url}
                    </span>
                  </span>
                </span>
                <ChevronRight size={16} className="shrink-0 text-primary-foreground/60" />
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
